<template>
  <component
    :is="tag"
    v-bind="elementAttrs"
    :class="classes"
    :style="themeVars"
    v-on="eventListeners"
  >
    <i v-if="icon" class="app-btn__icon" :class="`fa-regular ${icon}`" />
    <img v-if="iconSrc" class="app-btn__icon" :src="iconSrc" alt="" />
    <span class="app-btn__label">
      <slot />
    </span>
    <span v-if="showIndicator" class="app-btn__loader" aria-hidden="true">
      <span class="app-btn__dot" />
      <span class="app-btn__dot" />
      <span class="app-btn__dot" />
    </span>
  </component>
</template>

<script>
// Brand-aligned palette — mirrors the unified admin token set (slate neutrals
// + #1f73c1 brand + one value per semantic). `subtle` is the soft tint used by
// white/outline/ghost hover; `rgb` feeds the focus ring.
const THEMES = {
  primary:   { bg: '#1f73c1', hover: '#195a9b', subtle: '#e9f1fa', rgb: '31,115,193'   },
  success:   { bg: '#2da44e', hover: '#248a41', subtle: '#e9f6ee', rgb: '45,164,78'    },
  danger:    { bg: '#cf222e', hover: '#b51d28', subtle: '#fbe9ea', rgb: '207,34,46'    },
  warning:   { bg: '#c4801c', hover: '#a76d18', subtle: '#f8f0e2', rgb: '196,128,28'   },
  info:      { bg: '#0ea596', hover: '#0c8c80', subtle: '#e2f5f2', rgb: '14,165,150'   },
  secondary: { bg: '#6c7a89', hover: '#5b6675', subtle: '#eef1f4', rgb: '108,122,137' },
  purple:    { bg: '#6d46c1', hover: '#5d3aa8', subtle: '#f0ebf8', rgb: '109,70,193'   },
}

export default {
  name: 'AppButton',

  inheritAttrs: false,

  props: {
    theme: {
      type: String,
      default: 'primary',
      validator: (v) => Object.keys(THEMES).includes(v),
    },
    variant: {
      type: String,
      default: 'solid',
      validator: (v) => ['solid', 'outline', 'ghost', 'white'].includes(v),
    },
    size: {
      type: String,
      default: 'md',
      validator: (v) => ['sm', 'md', 'lg'].includes(v),
    },
    type: {
      type: String,
      default: 'button',
    },
    to: {
      type: String,
      default: '',
    },
    href: {
      type: String,
      default: '',
    },
    icon: {
      type: String,
      default: '',
    },
    iconSrc: {
      type: String,
      default: '',
    },
    showIndicator: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    block: {
      type: Boolean,
      default: false,
    },
    uppercase: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    tag() {
      if (this.to)   return 'router-link'
      if (this.href) return 'a'
      return 'button'
    },

    isDisabled() {
      return this.disabled || this.showIndicator
    },

    classes() {
      return [
        'app-btn',
        `app-btn--${this.size}`,
        `app-btn--${this.variant}`,
        {
          'app-btn--block':     this.block,
          'app-btn--uppercase': this.uppercase,
          'app-btn--loading':   this.showIndicator,
          'app-btn--disabled':  this.isDisabled,
        },
      ]
    },

    themeVars() {
      const t = THEMES[this.theme] || THEMES.primary
      return {
        '--btn-bg':     t.bg,
        '--btn-hover':  t.hover,
        '--btn-subtle': t.subtle,
        '--btn-color':  t.color || '#ffffff',
        '--btn-rgb':    t.rgb,
      }
    },

    elementAttrs() {
      const attrs = { ...this.$attrs }
      if (this.tag === 'button') {
        attrs.type     = this.type
        attrs.disabled = this.isDisabled || undefined
      }
      if (this.tag === 'router-link') {
        attrs.to = this.isDisabled ? '' : this.to
      }
      if (this.tag === 'a') {
        attrs.href = this.href
      }
      return attrs
    },

    eventListeners() {
      return {
        ...this.$listeners,
        click: (e) => {
          if (this.isDisabled) { e.preventDefault(); return }
          this.$emit('click', e)
        },
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.app-btn {
  // Layout
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  position: relative;

  // Typography
  font-family: inherit;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  text-decoration: none;

  // Interaction
  cursor: pointer;
  user-select: none;
  vertical-align: middle;

  // Shape
  border: 1px solid transparent;
  border-radius: 8px;
  box-sizing: border-box;

  // Transition
  transition: background 0.15s ease, border-color 0.15s ease,
              color 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;

  // Focus ring
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(var(--btn-rgb), 0.28);
  }

  // ── Sizes ──────────────────────────────────────────────────────────────────
  &--sm {
    height: 28px;
    padding: 0 10px;
    font-size: 12px;
    border-radius: 8px;
    gap: 5px;
  }

  &--md {
    height: 36px;   // matches c-input / form-control height
    padding: 0 14px;
    font-size: 13px;
  }

  &--lg {
    height: 44px;
    padding: 0 20px;
    font-size: 14px;
    border-radius: 8px;
    gap: 8px;
  }

  // ── Variants ────────────────────────────────────────────────────────────────
  &--solid {
    background: var(--btn-bg);
    color: var(--btn-color);
    border-color: var(--btn-bg);

    &:hover:not(.app-btn--disabled) {
      background: var(--btn-hover);
      border-color: var(--btn-hover);
    }
  }

  &--outline {
    background: transparent;
    color: var(--btn-bg);
    border-color: var(--btn-bg);

    &:hover:not(.app-btn--disabled) {
      background: var(--btn-subtle);
    }
  }

  &--ghost {
    background: transparent;
    color: var(--btn-bg);
    border-color: transparent;

    &:hover:not(.app-btn--disabled) {
      background: var(--btn-subtle);
    }
  }

  &--white {
    background: #ffffff;
    color: var(--btn-bg);
    border-color: var(--btn-bg);

    &:hover:not(.app-btn--disabled) {
      background: var(--btn-subtle);
    }
  }

  // ── Modifiers ───────────────────────────────────────────────────────────────
  &--block {
    width: 100%;
  }

  &--uppercase {
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &--disabled {
    opacity: 0.55;
    cursor: not-allowed;
    pointer-events: none;
  }

  &--loading {
    pointer-events: none;

    .app-btn__label { opacity: 0; }
    .app-btn__icon  { opacity: 0; }
  }

  // ── Slots ───────────────────────────────────────────────────────────────────
  &__label {
    display: contents;
  }

  &__icon {
    flex-shrink: 0;
    font-size: 0.9em;
    line-height: 1;

    img {
      display: block;
      width: 14px;
      height: 14px;
      object-fit: contain;
    }
  }

  // ── Loading dots ─────────────────────────────────────────────────────────────
  &__loader {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
  }

  &__dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: currentColor;
    opacity: 0;
    animation: app-btn-dot 1s ease infinite;

    &:nth-child(2) { animation-delay: 0.15s; }
    &:nth-child(3) { animation-delay: 0.30s; }
  }
}

@keyframes app-btn-dot {
  0%, 80%, 100% { opacity: 0; transform: scale(0.7); }
  40%            { opacity: 1; transform: scale(1); }
}
</style>
